/** DeskReplay (deskreplay.com) legal documents. Not legal advice; customers should involve counsel for their jurisdiction. */

export type PolicySection = { heading: string; paragraphs: string[]; anchorId?: string };
export type PolicyDoc = {
  title: string;
  lastUpdated: string;
  intro?: string;
  sections: PolicySection[];
};

const COMPANY = "DeskReplay";
const CONTACT_EMAIL = "legal@deskreplay.com";

/** Short closing line for policy documents (physical address not published on the marketing site). */
function contactBlock(): string {
  return `For questions about these terms, contact ${COMPANY} at ${CONTACT_EMAIL}.`;
}

export const LEGAL_POLICY_ORDER: { slug: string; label: string }[] = [
  { slug: "pricing-policy", label: "Pricing Policy" },
  { slug: "privacy-policy", label: "Privacy Policy" },
  { slug: "cookie-policy", label: "Cookie Policy" },
  { slug: "terms-of-service", label: "Terms of Service" },
  { slug: "disclaimer", label: "Disclaimer" },
  { slug: "refund-policy", label: "Refund Policy" },
  { slug: "cancellation-policy", label: "Cancellation Policy" },
  { slug: "software-license-agreement", label: "Software License Agreement" },
  { slug: "service-level-agreement", label: "Service Level Agreement" },
  { slug: "security-pledge", label: "Security Pledge" },
  { slug: "non-invasive-design-manifesto", label: "Non-Invasive Design Manifesto" },
];

export const legalPolicies: Record<string, PolicyDoc> = {
  "pricing-policy": {
    title: "Pricing Policy",
    lastUpdated: "May 5, 2026",
    intro: contactBlock(),
    sections: [
      {
        heading: "1. Overview",
        paragraphs: [
          `${COMPANY} offers subscription or term-based licensing for its enterprise visual session intelligence and related software (the “Software”), typically quoted per endpoint, workspace, or deployment tier. Published list prices, if any, are subject to change without notice until confirmed in an order form or written quote.`,
          "Fees may include platform subscription, optional professional services, and support tiers. Unless otherwise stated, quotes are valid for thirty (30) days.",
        ],
      },
      {
        heading: "2. Taxes and currency",
        paragraphs: [
          "Fees are exclusive of applicable taxes, levies, or duties, including VAT, GST, or withholding taxes, unless expressly included in a signed quote. You are responsible for any taxes imposed on your purchase, other than taxes based on our net income.",
          "Invoices are issued in the currency specified on your order. Currency conversion or bank fees charged by your institution are your responsibility.",
        ],
      },
      {
        heading: "3. Billing cycles and renewals",
        paragraphs: [
          "Unless your contract states otherwise, subscriptions renew at the end of each term at the then-current list price or renewal fee communicated at least sixty (60) days before renewal.",
          "Volume tiers and discounts are applied according to your executed agreement. Downgrades or seat reductions may take effect only at the next renewal unless explicitly agreed in writing.",
        ],
      },
      {
        heading: "4. Fair use and overages",
        paragraphs: [
          "Certain plans may include usage caps (for example, storage, retention, or API volume). If you exceed contracted limits, overage fees may apply as set forth in your order or we may request a plan upgrade.",
        ],
      },
      {
        heading: "5. Contact",
        paragraphs: [`Pricing questions: ${CONTACT_EMAIL}.`],
      },
    ],
  },

  "privacy-policy": {
    title: "Privacy Policy",
    lastUpdated: "October 15, 2026",
    intro: `This Privacy Policy describes how ${COMPANY} (“we,” “us”) handles personal data when you visit deskreplay.com, interact with our sales and support teams, or when we process limited vendor-side data in connection with your subscription. It also summarizes what categories of data the DeskReplay product is designed to process when you deploy it. Your deployment remains under your control when self-hosted.`,
    sections: [
      {
        heading: "1. Who we are",
        paragraphs: [
          `${COMPANY} provides self-hostable enterprise software for policy-driven visual session capture, streaming, search, and evidence governance (the “Software”). For website visits, demos, and commercial relationships, we act as a controller of the business contact information you provide. Business contact options are listed on our Contact page.`,
        ],
      },
      {
        heading: "2. Categories of data involved",
        paragraphs: [
          "DeskReplay.com and commercial relationship: account and contact data (name, business email, phone, company, role), billing details when you purchase, technical metadata from our marketing site (such as IP address, device type, browser, pages viewed), and support communications you send us.",
          "When you run the Software in your environment (customer-controlled): DeskReplay is built to process policy-scoped visual session evidence, for example encoded session streams, frames or clips aligned to triggers you define, timestamps and application/context metadata needed for playback and audit, optional text derived from on-screen content (such as transcripts or search indexes where you enable those features), configuration and policy definitions, and access logs for who queried or exported evidence. What is actually captured depends on your policies, integrations, and edition; it is not the same data model as legacy employee-monitoring suites that emphasize exhaustive keystroke logs, peripheral-device telemetry, or omnichannel activity feeds unrelated to workflow reconstruction.",
          "Vendor access: in a typical self-hosted deployment, production session content remains in your infrastructure. We do not access your production evidence except as expressly agreed (for example, time-bound remote support with your authorization).",
        ],
      },
      {
        heading: "3. Purposes and legal bases",
        paragraphs: [
          "We process personal data to perform contracts, respond to inquiries, operate and secure our services, comply with law, and pursue legitimate interests such as fraud prevention and product improvement, consistent with applicable regulations.",
        ],
      },
      {
        heading: "4. Sharing and subprocessors",
        paragraphs: [
          "We may share data with hosting, analytics, CRM, payment, and communications vendors bound by confidentiality and data-processing terms. Marketing-site subprocessors can include our hosting provider, Google Analytics (site measurement), Microsoft Clarity (session measurement and heatmaps), and Cloudflare Turnstile (CAPTCHA on forms when enabled).",
          "We do not sell personal information as defined under applicable U.S. state privacy laws. We do not use Google Analytics advertising features on deskreplay.com (advertising storage is denied in our configuration).",
          "For cookies and similar technologies on deskreplay.com, see our Cookie Policy at /legal/cookie-policy. Use Cookie preferences in the site footer to reject optional measurement or change your choice.",
        ],
      },
      {
        heading: "5. International transfers",
        paragraphs: [
          "If personal data is transferred across borders, we implement appropriate safeguards such as Standard Contractual Clauses or equivalent mechanisms where required.",
        ],
      },
      {
        heading: "6. Retention",
        paragraphs: [
          "We retain personal data only as long as necessary for the purposes above, including legal, tax, and accounting obligations. Retention periods for customer-controlled production data follow your deployment configuration and contract.",
        ],
      },
      {
        heading: "7. Your rights",
        paragraphs: [
          "Depending on your jurisdiction, you may have rights to access, rectify, delete, restrict, or object to processing, and to lodge a complaint with a supervisory authority. EEA, UK, and Swiss visitors may object to processing based on legitimate interests where applicable, including optional site measurement when you have not consented via our cookie banner.",
          "Contact privacy@deskreplay.com to exercise these rights. We respond within timeframes required by applicable law.",
        ],
      },
      {
        heading: "7a. U.S. residents (California and similar state laws)",
        anchorId: "us-residents",
        paragraphs: [
          "We describe categories collected on deskreplay.com in Section 2. We use them to operate the site, respond to inquiries, secure forms, and (if you allow optional measurement) understand aggregate traffic. We do not sell personal information as defined under the California Consumer Privacy Act (CCPA) as amended by the CPRA.",
          "Optional measurement (Google Analytics and Microsoft Clarity) runs only if you choose Essential + measurement or Accept all in our cookie banner, or unless you have enabled a recognized opt-out preference such as the Global Privacy Control (GPC) signal, which we treat as a request to disable optional measurement.",
          "California and other U.S. state residents may contact privacy@deskreplay.com to request access, deletion, or correction of personal information we hold about them, subject to legal exceptions. We will not discriminate against you for exercising privacy rights.",
        ],
      },
      {
        heading: "8. Contact",
        paragraphs: [`Privacy inquiries: privacy@deskreplay.com or ${CONTACT_EMAIL}.`],
      },
    ],
  },

  "cookie-policy": {
    title: "Cookie Policy",
    lastUpdated: "October 15, 2026",
    intro: `This Cookie Policy explains how ${COMPANY} (“we,” “us”) uses cookies and similar technologies on deskreplay.com (the “Marketing Site”). It should be read together with our Privacy Policy. This policy does not describe cookies set by the DeskReplay product console or agents in your environment; those are governed by your deployment and contract.`,
    sections: [
      {
        heading: "1. Why this matters",
        paragraphs: [
          "Laws in the European Economic Area, United Kingdom, India, and several U.S. states require transparency about cookies and similar technologies. We designed deskreplay.com to use few categories, explain them plainly, and ask for your choice once per browser (unless you change preferences or we update this policy materially).",
          "We do not run advertising or social-media tracking pixels on this site.",
        ],
      },
      {
        heading: "2. What are cookies and similar technologies?",
        paragraphs: [
          "Cookies are small text files stored on your device. We also use similar browser storage (for example localStorage) where noted below. “First-party” means set by deskreplay.com; “third-party” means set by a provider we embed (for example Cloudflare on pilot signup).",
        ],
      },
      {
        heading: "3. What we use on deskreplay.com today",
        paragraphs: [
          "As of the “Last updated” date above, we use two optional measurement tools when you consent: Google Analytics 4 (Google LLC) and Microsoft Clarity (Microsoft Corporation) for traffic measurement, session replay, and heatmaps. We do not load them if you reject optional measurement.",
        ],
      },
      {
        heading: "3a. Strictly necessary",
        paragraphs: [
          "Purpose: Remember your cookie choice, deliver pages securely, operate the site, and protect forms against automated abuse when our API enforces a challenge.",
          "Examples: Consent preference stored in your browser (localStorage key deskreplay_cookie_consent_v2). Hosting and content-delivery cookies that our infrastructure provider may set to route traffic and protect the site (for example session or security cookies from Vercel or comparable hosts). Cloudflare Turnstile on /get-started, /contact, and /request-demo when enabled (script from challenges.cloudflare.com).",
          "Legal basis (EEA/UK): Strictly necessary to provide the service you request or for security, depending on the specific technology.",
          "Retention: Consent preference persists for up to twelve (12) months, until you clear site data, until we publish a material policy change (internal policy revision), or until you update Cookie preferences in the footer. CAPTCHA provider retention is governed by Cloudflare’s policies.",
        ],
      },
      {
        heading: "3b. Measurement (optional)",
        paragraphs: [
          "Purpose: Understand aggregate traffic and improve the Marketing Site.",
          "Examples: Google Analytics 4 and Microsoft Clarity. These run only if you choose Essential + measurement or Accept all in the cookie banner.",
          "Legal basis (EEA/UK/India): Your consent via the banner (or equivalent choice). If you reject optional measurement, we do not load these tools on that browser.",
          "Retention: Governed by Google and Microsoft policies; see their documentation for cookie lifetimes.",
        ],
      },
      {
        heading: "4. Your choices on this site",
        paragraphs: [
          "When you first visit deskreplay.com, a bottom banner offers three choices with equal prominence: Reject optional measurement, Essential + measurement, and Accept all. Reject stores your choice and does not load Google Analytics or Microsoft Clarity. Essential + measurement and Accept all both enable those tools today; Accept all is reserved if we add further optional categories later.",
          "If you reject optional measurement, core pages and forms still work, but we cannot measure or improve the site from your visit. If you block all cookies in your browser settings, the banner may reappear and some features (including registration) may not work reliably.",
          "How often we ask: after you save a choice, we store it in your browser and do not show the banner on every visit. We may ask again after about twelve (12) months, if we materially update this Cookie Policy (we bump an internal policy revision), or if you clear site data.",
          "Cookie preferences in the footer opens a panel where you can switch to Reject, Essential + measurement, or Accept all at any time. Choosing Reject stops new measurement immediately; previously set third-party cookies may remain until you clear them in browser settings.",
          "Global Privacy Control: if your browser sends a recognized GPC opt-out signal and you have not already saved a cookie choice, we treat that as Reject optional measurement.",
          "More information: Google Analytics privacy documentation at policies.google.com/privacy and Microsoft Clarity terms at clarity.microsoft.com/terms.",
        ],
      },
      {
        heading: "5. How to control cookies in your browser",
        paragraphs: [
          "You can block or delete cookies through browser settings. Blocking all cookies may prevent the consent banner from remembering your choice and can affect how forms behave. Help links: Chrome, Firefox, Safari, and Edge publish cookie controls in their support centers.",
        ],
      },
      {
        heading: "6. Other DeskReplay properties",
        paragraphs: [
          "live.deskreplay.com (admin console) and customer deployments use separate authentication, session, and security mechanisms governed by your organization’s agreement and notices. Do not rely on this Cookie Policy alone for product-side processing.",
        ],
      },
      {
        heading: "7. International visitors",
        paragraphs: [
          "If you are in the EEA, UK, or Switzerland, you may have additional rights under GDPR/UK GDPR, including to withdraw consent to optional measurement via Cookie preferences. If you are in India, optional measurement relies on your consent through the banner, consistent with notice expectations under the Digital Personal Data Protection Act for non-essential processing.",
        ],
      },
      {
        heading: "8. Updates",
        paragraphs: [
          "We may update this Cookie Policy when our site technologies change. We will revise the “Last updated” date and, for material changes, provide a clear notice on the Marketing Site.",
        ],
      },
      {
        heading: "9. Contact",
        paragraphs: [
          `Cookie and privacy questions: privacy@deskreplay.com or ${CONTACT_EMAIL}.`,
        ],
      },
    ],
  },

  "terms-of-service": {
    title: "Terms of Service",
    lastUpdated: "May 5, 2026",
    intro: `These Terms of Service (“Terms”) govern access to ${COMPANY} websites and, together with an executed order form or license agreement, the Software. By using our services or signing an order, you agree to these Terms.`,
    sections: [
      {
        heading: "1. License",
        paragraphs: [
          "Subject to payment and compliance, we grant you a non-exclusive, non-transferable license to use the Software within the scope, seat count, and environment described in your order. Self-hosted deployments must remain within infrastructure you control as specified in documentation.",
        ],
      },
      {
        heading: "2. Acceptable use",
        paragraphs: [
          "You will use the Software only in compliance with law and your internal policies. You must obtain all notices, consents, works-council or union steps, and authorizations required for policy-scoped visual session capture, retention of evidence, and downstream use (audit, dispute resolution, investigations) in each jurisdiction where you deploy.",
          "You agree not to configure DeskReplay to collect categories of personal data beyond what your lawful basis and notices cover. You may not reverse engineer except where prohibited restrictions are unenforceable under law.",
        ],
      },
      {
        heading: "3. Your data",
        paragraphs: [
          "You retain ownership of your data. We process personal data as described in our Privacy Policy and any data processing agreement. You are responsible for classification, retention, and lawful basis for processing in your organization.",
        ],
      },
      {
        heading: "4. Confidentiality",
        paragraphs: [
          "Each party may receive confidential information from the other. The receiving party will protect such information with reasonable care and use it only for the purposes of the agreement.",
        ],
      },
      {
        heading: "5. Warranties and disclaimer",
        paragraphs: [
          "Except as expressly stated in an order, the Software is provided “as is.” See our Disclaimer for further limitation of warranties.",
        ],
      },
      {
        heading: "6. Limitation of liability",
        paragraphs: [
          "To the maximum extent permitted by law, neither party is liable for indirect, incidental, special, consequential, or punitive damages, or loss of profits, data, or goodwill. Our aggregate liability arising out of these Terms is limited to fees paid by you in the twelve (12) months preceding the claim, except where liability cannot be limited by law.",
        ],
      },
      {
        heading: "7. Term and termination",
        paragraphs: [
          "These Terms continue until terminated. We may suspend access for material breach after reasonable notice when practicable. Provisions that by nature should survive will survive termination.",
        ],
      },
      {
        heading: "8. Governing law",
        paragraphs: [
          "Unless your master agreement specifies otherwise, these Terms are governed by the laws of India, without regard to conflict-of-law rules, and courts in Gurugram, Haryana shall have exclusive jurisdiction, subject to mandatory consumer protections where applicable.",
        ],
      },
      {
        heading: "9. Contact",
        paragraphs: [contactBlock()],
      },
    ],
  },

  disclaimer: {
    title: "Disclaimer",
    lastUpdated: "May 5, 2026",
    intro: `The information on deskreplay.com and in ${COMPANY} documentation is provided for general business and technical understanding only.`,
    sections: [
      {
        heading: "1. No warranty",
        paragraphs: [
          `${COMPANY} makes no representations or warranties of any kind, express or implied, regarding the completeness, accuracy, reliability, or suitability of website content. Your use of any information is at your own risk.`,
        ],
      },
      {
        heading: "2. Not legal or compliance advice",
        paragraphs: [
          "Nothing on this site constitutes legal, regulatory, or compliance advice. Deploying policy-driven visual session intelligence touches employment law, privacy, sector rules (financial services, healthcare, etc.), and contractual duties. You should consult qualified counsel for your obligations; DeskReplay’s feature set differs materially from older “monitoring” products that emphasize exhaustive activity logging unrelated to visual workflow evidence.",
        ],
      },
      {
        heading: "3. Third-party references",
        paragraphs: [
          "References to standards (such as GDPR, HIPAA, SOC 2) describe design goals or common customer use cases; they are not certifications unless expressly stated in a written attestation for your organization.",
        ],
      },
      {
        heading: "4. Forward-looking statements",
        paragraphs: [
          "Roadmaps, performance figures, and capability descriptions may change. Metrics on marketing pages are illustrative unless tied to a specific contractually committed benchmark.",
        ],
      },
      {
        heading: "5. Contact",
        paragraphs: [contactBlock()],
      },
    ],
  },

  "refund-policy": {
    title: "Refund Policy",
    lastUpdated: "May 8, 2026",
    intro: `${COMPANY} does not issue refunds on subscription fees, license fees, or related professional services, except as described below. We intend for you to decide with confidence: we offer trial, pilot, and evaluation periods (paid or unpaid, as described in your offer) so you can test fit, performance, and governance before you commit to a production subscription.`,
    sections: [
      {
        heading: "1. Trials, evaluation, and why we do not refund",
        paragraphs: [
          "You are expected to use the trial or evaluation window to validate DeskReplay against your workflows, security requirements, and procurement process. Once you place an order and we provision licenses, allocate capacity, and activate your subscription term, fees are earned and consumed for that period.",
          "Paid pilots or evaluations, where offered, are governed by the pilot or evaluation terms we provide (including whether fees may be credited toward a later production order). Those terms do not create a general right to a refund after you move to a paid subscription.",
          "Except where applicable law requires otherwise, or your executed order form or master agreement expressly promises a refund or money-back term, DeskReplay does not refund fees that have been invoiced and paid. This is our standard position after you have had a fair opportunity to evaluate the product beforehand.",
        ],
      },
      {
        heading: "2. When a signed contract differs",
        paragraphs: [
          "If your order form, statement of work, or master agreement includes a specific refund, credit, or pilot clause, those signed terms control for that transaction.",
        ],
      },
      {
        heading: "3. Billing corrections",
        paragraphs: [
          "If you believe an invoice duplicates a charge, uses an incorrect quantity or rate, or reflects another clear processing mistake, contact billing within thirty (30) days of the invoice date. We will investigate in good faith and correct confirmed billing errors (for example, by reissuing an invoice or applying an offset). A billing correction is not a discretionary refund of fees for service you have chosen to discontinue.",
        ],
      },
      {
        heading: "4. Chargebacks and payment disputes",
        paragraphs: [
          "Please contact us before initiating a card chargeback or bank dispute so we can review the charge with you. Chargebacks may result in suspension of service until the matter is resolved.",
        ],
      },
      {
        heading: "5. Summary and contact",
        paragraphs: [
          `In summary: ${COMPANY} does not provide refunds on subscription or license fees because we structure trial and evaluation time upfront so you can make a final purchasing decision with full information. For questions, contact billing@deskreplay.com or your account contact. Policy questions: ${CONTACT_EMAIL}.`,
        ],
      },
    ],
  },

  "cancellation-policy": {
    title: "Cancellation Policy",
    lastUpdated: "May 5, 2026",
    intro: `This Cancellation Policy describes how to cancel ${COMPANY} subscriptions and what happens to your deployment when you do.`,
    sections: [
      {
        heading: "1. How to cancel",
        paragraphs: [
          "To cancel a subscription, provide written notice to your account manager or to billing@deskreplay.com, identifying your organization and subscription. Cancellation for renewal requires notice before the renewal date as specified in your contract (often at least thirty (30) days).",
        ],
      },
      {
        heading: "2. Effect of cancellation",
        paragraphs: [
          "Cancellation stops future renewals; it does not typically refund fees for the current prepaid period unless your agreement says otherwise. Access to support and updates ends when the subscription term ends.",
        ],
      },
      {
        heading: "3. Self-hosted deployments",
        paragraphs: [
          "For self-hosted installations, you are responsible for uninstalling agents and securely deleting stored sessions according to your retention policies. We may provide limited wind-down assistance if purchased separately.",
        ],
      },
      {
        heading: "4. Data export",
        paragraphs: [
          "Before shutdown, you should export evidence and configuration you require. Features available for export depend on your product version and contract.",
        ],
      },
      {
        heading: "5. Contact",
        paragraphs: [contactBlock()],
      },
    ],
  },

  "software-license-agreement": {
    title: "Software License Agreement",
    lastUpdated: "May 5, 2026",
    intro: `This Software License Agreement (“SLA-License”) supplements your order. Capitalized terms match the Terms of Service unless defined here.`,
    sections: [
      {
        heading: "1. Grant",
        paragraphs: [
          `${COMPANY} grants you a limited license to install and run the Software solely for your internal business purposes, for the licensed quantity and term, and only on systems you own or control.`,
        ],
      },
      {
        heading: "2. Restrictions",
        paragraphs: [
          "You may not rent, lease, sublicense, or host the Software for third parties except as authorized in writing. You may not remove proprietary notices. You may not exceed licensed capacity or circumvent technical protections.",
        ],
      },
      {
        heading: "3. Open source",
        paragraphs: [
          "The Software may include open-source components governed by their respective licenses, copies of which may be provided in documentation or on request.",
        ],
      },
      {
        heading: "4. Updates",
        paragraphs: [
          "We may provide updates, patches, or new versions during an active subscription. Updates are licensed under the same terms unless accompanied by a separate agreement.",
        ],
      },
      {
        heading: "5. Audit",
        paragraphs: [
          "On reasonable notice, you will allow us to verify deployment counts and compliance with license limits, remotely where possible, subject to confidentiality.",
        ],
      },
      {
        heading: "6. Contact",
        paragraphs: [contactBlock()],
      },
    ],
  },

  "service-level-agreement": {
    title: "Service Level Agreement",
    lastUpdated: "May 5, 2026",
    intro: `This Service Level Agreement (“SLA”) describes uptime and support targets for ${COMPANY}-managed services where applicable. Self-hosted deployments in your VPC are governed by your infrastructure and operations; separate professional services may define operational targets.`,
    sections: [
      {
        heading: "1. Scope",
        paragraphs: [
          "This SLA applies only to services explicitly identified in your order as hosted or managed by us. On-premises or customer-cloud deployments are excluded unless a separate addendum sets metrics (for example, response times for support tickets).",
        ],
      },
      {
        heading: "2. Availability target",
        paragraphs: [
          "For covered hosted control planes, we target 99.9% monthly uptime, excluding scheduled maintenance announced at least forty-eight (48) hours in advance, force majeure, internet outages outside our network, or issues caused by your configuration or third-party services.",
        ],
      },
      {
        heading: "3. Support tiers",
        paragraphs: [
          "Support hours, initial response targets, and escalation paths depend on your support tier (Standard, Premium, or bespoke). Details are listed in your order or support appendix.",
        ],
      },
      {
        heading: "4. Service credits",
        paragraphs: [
          "If monthly uptime falls below the target solely due to our infrastructure fault, you may request service credits as described in your order. Credits are your exclusive remedy for SLA failures unless mandatory law provides otherwise.",
        ],
      },
      {
        heading: "5. Contact",
        paragraphs: [`support@deskreplay.com · ${CONTACT_EMAIL}`],
      },
    ],
  },

  "security-pledge": {
    title: "Security Pledge",
    lastUpdated: "May 5, 2026",
    intro: `${COMPANY} is built for organizations that cannot afford ambiguous evidence or uncontrolled data paths. This pledge summarizes our security posture commitments at a product-design level.`,
    sections: [
      {
        heading: "1. Self-hosting first",
        paragraphs: [
          "We architect the platform so session streams, storage, and indexing can remain inside your VPC or data center. You choose regions, encryption keys, and network controls.",
        ],
      },
      {
        heading: "2. Least privilege",
        paragraphs: [
          "Agents and services operate with minimal permissions required for capture and delivery. Administrative actions should be RBAC-controlled and audited.",
        ],
      },
      {
        heading: "3. Evidence integrity",
        paragraphs: [
          "We design for tamper-evident chains of custody and configurable retention so forensic workflows remain defensible.",
        ],
      },
      {
        heading: "4. Responsible disclosure",
        paragraphs: [
          `Report security issues to security@deskreplay.com. We commit to timely triage and coordinated disclosure for validated vulnerabilities.`,
        ],
      },
      {
        heading: "5. Contact",
        paragraphs: [contactBlock()],
      },
    ],
  },

  "non-invasive-design-manifesto": {
    title: "Non-Invasive Design Manifesto",
    lastUpdated: "May 5, 2026",
    intro: `${COMPANY} exists to reduce organizational risk without turning every workstation into a surveillance panopticon. Our design philosophy favors proportionality, transparency, and operator dignity.`,
    sections: [
      {
        heading: "1. Policy-first capture",
        paragraphs: [
          "Recording should be triggered by clear policies (applications, scenarios, or risk signals), not by default continuous filming of personal life on corporate devices where avoidable.",
        ],
      },
      {
        heading: "2. Search over surveillance",
        paragraphs: [
          "The goal is rapid answers to legitimate questions (“show me the wire confirmation screen at 14:03”), not endless human review of full-motion video.",
        ],
      },
      {
        heading: "3. Purpose limitation",
        paragraphs: [
          "Access to recordings should be role-limited, justified, and logged. HR, legal, and security each have distinct boundaries.",
        ],
      },
      {
        heading: "4. Worker trust",
        paragraphs: [
          "Employers should communicate monitoring policies clearly and comply with notice requirements. Technology must support compliance, not replace it.",
        ],
      },
      {
        heading: "5. Contact",
        paragraphs: [contactBlock()],
      },
    ],
  },
};
